import { Check, Trophy } from '../ui/icons';
import { signedPoints } from '../../lib/format';
import { cn } from '../../lib/cn';
import type { PickOutcome } from '../../lib/pickOutcome';

/** Points earned (or live projection) on a built card — compact for slate rows, large for overview cards. */
export function PickPointsBadge({
  outcome,
  size = 'compact'
}: {
  outcome: PickOutcome;
  size?: 'compact' | 'large';
}) {
  const large = size === 'large';
  const label = outcome.live ? 'live' : outcome.perfect ? 'perfect' : 'scored';

  if (outcome.perfect && !outcome.live) {
    return (
      <div className={cn('text-right', large && 'min-w-[88px]')}>
        <span
          className={cn(
            'num relative inline-flex items-center overflow-hidden font-extrabold text-white',
            large
              ? 'gap-2 rounded-[10px] bg-[linear-gradient(135deg,#2EDB7B,#0BA078)] py-[7px] pl-2 pr-[11px] text-[15px] shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_8px_18px_-8px_rgba(11,160,120,0.75)]'
              : 'gap-1.5 rounded-[9px] bg-[linear-gradient(135deg,#2EDB7B,#0BA078)] py-[5px] pl-1.5 pr-[9px] text-[11.5px] shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_6px_14px_-6px_rgba(11,160,120,0.8)]'
          )}
        >
          <span className="pointer-events-none absolute inset-y-0 w-[42%] animate-h2h-sheen bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.5),transparent)]" />
          <span
            className={cn(
              'relative grid shrink-0 place-items-center rounded-full bg-white/25',
              large ? 'h-[18px] w-[18px]' : 'h-[15px] w-[15px]'
            )}
          >
            <Trophy width={large ? 10 : 9} height={large ? 10 : 9} strokeWidth={2.6} className="text-white" />
          </span>
          <span className="relative tracking-[-0.01em]">{signedPoints(outcome.points)}</span>
        </span>
        {large && (
          <div className="mt-[2px] text-[9px] font-bold uppercase tracking-[0.04em] text-accent-ink">{label}</div>
        )}
      </div>
    );
  }

  const positive = outcome.points > 0;
  const live = outcome.live;

  return (
    <div className={cn('text-right', large && 'min-w-[72px]')}>
      <span
        className={cn(
          'num inline-flex items-center font-extrabold tracking-[-0.02em]',
          large ? 'gap-2 text-[22px] leading-none' : 'gap-1.5 rounded-[9px] py-[5px] text-[11.5px]',
          !large &&
            (positive
              ? 'bg-[rgba(36,197,106,0.14)] pl-1.5 pr-[9px] text-accent-ink'
              : 'bg-fill px-[9px] text-ink3'),
          large && (positive ? 'text-[#0A9A72]' : live ? 'text-[#C0392B]' : 'text-ink2')
        )}
      >
        {positive && !large && (
          <span className="grid h-[15px] w-[15px] shrink-0 place-items-center rounded-full bg-accent">
            <Check width={9} height={9} strokeWidth={4} className="text-white" />
          </span>
        )}
        {live && large && <span className="h-[6px] w-[6px] animate-h2h-pulse rounded-full bg-[#EF4135]" />}
        <span>{signedPoints(outcome.points)}</span>
        {!large && <span className="text-[10px] font-bold tracking-wide">{live ? 'live' : 'pts'}</span>}
      </span>
      {large && (
        <div
          className={cn(
            'mt-[2px] text-[9px] font-bold uppercase tracking-[0.04em]',
            live ? 'text-[#C0392B]' : positive ? 'text-accent-ink' : 'text-ink3'
          )}
        >
          {label}
        </div>
      )}
    </div>
  );
}
